import { useCallback, useMemo } from 'react';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs/selectors';

import { OptionValue } from '@/types/components/common';
import { CountryISO } from '@/types/places';

import { isTrinidadOrTobagoUser } from '@/utils/user';

export function useAddressCityList() {
  const { t } = useTranslate();
  const { addressCityOptions } = useAppSelector(selectConfigsState);
  const user = useAppSelector(selectLoggedInUser);
  const isTTUser = isTrinidadOrTobagoUser(user);

  const isCityListAvailable = useCallback(
    (country: string | undefined) =>
      !!addressCityOptions?.length &&
      (!isTTUser || country === CountryISO.Trinidad),
    [addressCityOptions, isTTUser],
  );

  const cityOptions = useMemo(
    () => [
      ...(addressCityOptions || []),
      {
        text: t('createNewAccount.addressPage.form.city.noneOption'),
        value: OptionValue.None,
      },
    ],
    [addressCityOptions, t],
  );

  const resolveCityFromPlace = useCallback(
    (city: string | undefined, country: string | undefined) => {
      if (!city) {
        return '';
      }
      if (!isCityListAvailable(country)) {
        return city;
      }
      return addressCityOptions?.some(({ value }) => value === city)
        ? city
        : '';
    },
    [addressCityOptions, isCityListAvailable],
  );

  return {
    isCityListAvailable,
    cityOptions,
    addressCityOptions,
    resolveCityFromPlace,
  };
}
